import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import {
  cueArrowFullRight,
  cueExchange,
  cueExclamation,
  cueLock,
  cueLogin,
  cueUser,
  IconComponent,
} from '@cue/ui/icons';

import { translation } from '@cue/translations';
import { PasswordComponent, TextboxComponent } from '@cue/assist/ui/inputs';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService, ConfigService } from '@assist/shared/data';
import { ApiService, emailLogin, ExternalLoginCA, loginEWS, userHasPassword, userLogin } from '@cue/api';
import { filter, first, tap } from 'rxjs/operators';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'assist-login-form',
  imports: [IconComponent, PasswordComponent, ReactiveFormsModule, TextboxComponent, TranslocoPipe, RouterLink],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginFormComponent implements OnInit {
  protected readonly translation = translation;
  authService = inject(AuthService);
  apiService = inject(ApiService);
  configService = inject(ConfigService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  loginMode = this.configService.value.serverType;
  serverError = signal<any | null>(null);
  exchangeErrorDetails = signal<{ code: string; requestId: string | null } | null>(null);
  touchoneLoginStep = signal<'email' | 'password'>('email');
  loading = signal(false);
  ICONS = {
    cueExclamation,
    cueLock,
    cueUser,
    cueExchange,
    cueArrowFullRight,
    cueLogin,
  };

  fb = inject(FormBuilder);

  loginForm: FormGroup<{
    email: FormControl<string>;
    password: FormControl<string>;
  }> = this.fb.group({
    email: ['', Validators.required],
    password: ['', this.configService.value.serverType === 'touchone-calendar' ? [] : [Validators.required]],
  });

  get passwordControl() {
    return this.loginForm.controls.password;
  }

  get emailControl() {
    return this.loginForm.controls.email;
  }

  ngOnInit() {
    this.activatedRoute.queryParams
      .pipe(
        filter((x) => x['error'] === 'login_unsuccessful'),
        first(),
        tap(() => {
          this.serverError.set(this.translocoService.translate(translation.account.unsuccessfulLogin));
        })
      )
      .subscribe();
  }

  loginOAuth() {
    this.authService.redirectToAuthorize();
  }

  loginRedirect(connectionAgent: ExternalLoginCA) {
    this.authService.loginWithExternalIdentity(connectionAgent);
  }

  customReturnUrl = input<string>();
  setCustomReturnUrlToCookie = explicitEffect([this.customReturnUrl], ([customReturnUrl]) => {
    if (customReturnUrl) {
      sessionStorage.setItem('redirect_url', customReturnUrl);
    }
  });

  onLogin() {
    this.serverError.set(null);
    this.exchangeErrorDetails.set(null);
    if (this.loginForm.valid) {
      this.apiService
        .call(
          loginEWS({
            username: this.loginForm.value.email,
            password: this.loginForm.value.password,
          })
        )
        .pipe(
          tap((resp) => {
            if (resp.success) {
              this.authService.saveApiToken(resp.data.token);
              this.authService.saveApiRefreshToken(resp.data.refreshToken);
            }
          })
        )
        .subscribe(
          (x) => {
            if (x.success) {
              this.serverError.set(null);
              const redirectUrl = sessionStorage.getItem('redirect_url');
              if (redirectUrl !== null) {
                sessionStorage.removeItem('redirect_url');
                this.router.navigateByUrl(redirectUrl);
              } else {
                this.router.navigate(['/dashboard']);
              }
            }
          },
          (error: HttpErrorResponse) => this.showExchangeLoginError(error)
        );
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  private showExchangeLoginError(error: HttpErrorResponse) {
    if (error.status === 0) {
      this.serverError.set(this.translocoService.translate(translation.account.exchangeApiUnavailable));
      return;
    }
    const messages: Record<string, string> = {
      EXCHANGE_UNAVAILABLE: translation.account.exchangeUnavailable,
      EXCHANGE_ACCOUNT_NOT_FOUND: translation.account.exchangeAccountNotFound,
      EXCHANGE_CREDENTIALS_REJECTED: translation.account.exchangeCredentialsRejected,
      ACCOUNT_DEACTIVATED: translation.account.exchangeAccountDeactivated,
      EXCHANGE_CONFIGURATION_ERROR: translation.account.exchangeConfigurationError,
      EXCHANGE_ACCOUNT_AMBIGUOUS: translation.account.exchangeAccountAmbiguous,
      LOGIN_INTERNAL_ERROR: translation.account.exchangeInternalError,
    };
    const receivedCode = error.error?.errorCode;
    const code =
      typeof receivedCode === 'string' && Object.prototype.hasOwnProperty.call(messages, receivedCode)
        ? receivedCode
        : 'LOGIN_INTERNAL_ERROR';
    const requestId = typeof error.error?.requestId === 'string' ? error.error.requestId : null;
    this.serverError.set(this.translocoService.translate(messages[code]));
    this.exchangeErrorDetails.set({ code, requestId });
  }

  continueToPasswordStep() {
    this.serverError.set(null);
    if (this.emailControl.valid) {
      this.loading.set(true);
      this.apiService
        .call(
          userHasPassword({
            email: this.emailControl.value,
          })
        )
        .subscribe(
          (response) => {
            this.loading.set(false);
            if (!response.success) {
              this.serverError.set(
                response.errorDescription || this.translocoService.translate(translation.account.unknownError)
              );
              return;
            }
            if (response.data?.hasPassword) {
              this.touchoneLoginStep.set('password');
              this.passwordControl.setValidators([Validators.required]);
              this.passwordControl.updateValueAndValidity();
            } else {
              this.onLoginPhoenix(true);
            }
          },
          () => {
            this.loading.set(false);
            this.serverError.set(this.translocoService.translate(translation.account.unknownError));
          }
        );
      return;
    }
    this.emailControl.markAsTouched();
  }

  onTouchonePasswordLogin() {
    this.serverError.set(null);
    if (this.passwordControl.invalid || this.emailControl.invalid) {
      this.emailControl.markAsTouched();
      this.passwordControl.markAsTouched();
      return;
    }
    this.loading.set(true);
    this.apiService
      .call(
        userLogin({
          email: this.emailControl.value,
          password: this.passwordControl.value,
        })
      )
      .subscribe(
        (response) => {
          this.loading.set(false);
          if (response.success && response.data?.token && response.data.refreshToken) {
            this.authService.saveApiToken(response.data.token);
            this.authService.saveApiRefreshToken(response.data.refreshToken);
            const redirectUrl = sessionStorage.getItem('redirect_url');
            if (redirectUrl !== null) {
              sessionStorage.removeItem('redirect_url');
              this.router.navigateByUrl(redirectUrl);
            } else {
              this.router.navigate(['/dashboard']);
            }
            return;
          }
          if (response.errorCode === 3085) {
            this.serverError.set(this.translocoService.translate(translation.account.wrongCreddentials));
            return;
          }
          if (response.errorCode === 3189) {
            this.serverError.set(this.translocoService.translate(translation.account.accountLocked));
            return;
          }
          this.serverError.set(
            response.errorDescription || this.translocoService.translate(translation.account.unknownError)
          );
        },
        (error) => {
          this.loading.set(false);
          this.serverError.set(
            error.status == 401
              ? this.translocoService.translate(translation.account.wrongCreddentials)
              : this.translocoService.translate(translation.account.unknownError)
          );
        }
      );
  }

  onLoginPhoenix(showPasswordSetupHint = false) {
    if (this.emailControl.valid) {
      this.loading.set(true);
      this.apiService.call(emailLogin(this.emailControl.value)).subscribe(
        (res) => {
          this.loading.set(false);
          this.router.navigate(['/account/email-link'], {
            queryParams: {
              id: res,
              email: this.emailControl.value,
              noPassword: showPasswordSetupHint ? '1' : undefined,
            },
          });
        },
        (error) => {
          this.loading.set(false);
          if (error?.status === 500) {
            this.serverError.set(this.translocoService.translate(translation.common.internalError));
          } else {
            this.serverError.set(error);
          }
        }
      );
    } else {
      this.emailControl.markAsTouched();
    }
  }

  onTouchoneSubmit() {
    if (this.touchoneLoginStep() === 'password') {
      this.onTouchonePasswordLogin();
      return;
    }
    this.continueToPasswordStep();
  }
}
